import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Star, Heart, ChevronLeft, ExternalLink, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import MapView from '../components/MapView';

import { useFavorites } from '../context/FavoritesContext';
import { ALL_SPAS } from '../data/spaData';

const SpaDetails = () => {
    const { id } = useParams();
    const [selectedImage, setSelectedImage] = useState(0);
    const { isFavorite, toggleFavorite } = useFavorites();

    // Find spa from static data
    const spa = useMemo(() => {
        return ALL_SPAS.find(s => s.id === id);
    }, [id]);

    const renderStars = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;

        for (let i = 0; i < 5; i++) {
            if (i < fullStars) {
                stars.push(
                    <Star key={i} className="w-5 h-5 fill-[#C8A86B] text-[#C8A86B]" />
                );
            } else if (i === fullStars && hasHalfStar) {
                stars.push(
                    <Star key={i} className="w-5 h-5 fill-[#C8A86B]/50 text-[#C8A86B]" />
                );
            } else {
                stars.push(
                    <Star key={i} className="w-5 h-5 text-[#E0DAD0]" />
                );
            }
        }
        return stars;
    };

    if (!spa) {
        return (
            <div className="min-h-screen bg-[#F9F6F0] pt-24 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">Spa not found</h2>
                    <Link to="/spas" className="btn-gold">
                        Browse All Spas
                    </Link>
                </div>
            </div>
        );
    }

    const favorite = isFavorite(spa.id);

    // Build SEO strings safely
    const seoTitle = `${spa.name} - ${spa.spa_type} in ${spa.area}`;
    const seoDesc = `${spa.name} in ${spa.area}, Bangalore. ${spa.rating} star rating with ${spa.reviews_count} reviews. ${spa.description}`;
    const seoKeywords = `${spa.name}, ${spa.spa_type} ${spa.area}, spa ${spa.area} Bangalore`;

    return (
        <div className="min-h-screen bg-[#F9F6F0] pt-24 noise-overlay" data-testid="spa-details-page">
            <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Back Button */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <Link 
                        to="/spas" 
                        className="inline-flex items-center gap-2 text-[#4A4E4D]/60 hover:text-[#C8A86B] transition-colors mb-8"
                        data-testid="back-to-spas"
                    >
                        <ChevronLeft className="w-5 h-5" />
                        Back to Spas
                    </Link>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                    {/* Image Gallery */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="relative rounded-2xl overflow-hidden mb-4">
                            <img
                                src={spa.images[selectedImage]}
                                alt={spa.name}
                                className="w-full aspect-[4/3] object-cover"
                            />
                            <button
                                onClick={() => toggleFavorite(spa.id)}
                                className={`absolute top-4 right-4 p-3 rounded-full transition-all duration-200 ${
                                    favorite
                                        ? 'bg-[#C8A86B] text-white'
                                        : 'bg-white/90 text-[#4A4E4D] hover:bg-[#C8A86B] hover:text-white'
                                }`}
                                data-testid="favorite-btn"
                            >
                                <Heart className={`w-5 h-5 ${favorite ? 'fill-current' : ''}`} />
                            </button>
                            <div className="absolute bottom-4 left-4">
                                <span className="px-4 py-1.5 bg-white/90 backdrop-blur-sm text-[#4A4E4D] text-sm font-medium rounded-full">
                                    {spa.spa_type}
                                </span>
                            </div>
                        </div>
                        {spa.images.length > 1 && (
                            <div className="flex gap-3">
                                {spa.images.map((img, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setSelectedImage(index)}
                                        className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                                            selectedImage === index
                                                ? 'border-[#C8A86B] opacity-100'
                                                : 'border-transparent opacity-60 hover:opacity-100'
                                        }`}
                                    >
                                        <img
                                            src={img}
                                            alt={`${spa.name} ${index + 1}`}
                                            className="w-full h-full object-cover"
                                        />
                                    </button>
                                ))}
                            </div>
                        )}
                    </motion.div>

                    {/* Spa Info */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        <h1 className="font-heading text-3xl sm:text-4xl text-[#4A4E4D] mb-4" data-testid="spa-name">
                            {spa.name}
                        </h1>

                        <div className="flex items-center gap-4 mb-6">
                            <div className="flex items-center gap-2">
                                <div className="flex">{renderStars(spa.rating)}</div>
                                <span className="font-medium text-[#4A4E4D]">{spa.rating}</span>
                            </div>
                            <span className="text-[#4A4E4D]/50">({spa.reviews_count} reviews)</span>
                            <span className="text-[#C8A86B] font-semibold">{spa.price_range}</span>
                        </div>

                        <p className="text-[#4A4E4D]/70 leading-relaxed mb-8">
                            {spa.description}
                        </p>

                        {/* Contact Info */}
                        <div className="space-y-4 mb-8">
                            <div className="flex items-start gap-3">
                                <MapPin className="w-5 h-5 text-[#722F37] flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-[#4A4E4D]">{spa.address}</p>
                                    <p className="text-[#4A4E4D]/60 text-sm">{spa.area}, Bangalore</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Phone className="w-5 h-5 text-[#722F37] flex-shrink-0" />
                                <a href={`tel:${spa.phone}`} className="text-[#4A4E4D] hover:text-[#722F37] transition-colors">
                                    {spa.phone}
                                </a>
                            </div>
                            <div className="flex items-center gap-3">
                                <Clock className="w-5 h-5 text-[#722F37] flex-shrink-0" />
                                <span className="text-[#4A4E4D]">{spa.opening_hours}</span>
                            </div>
                        </div>

                        {/* CTAs */}
                        <div className="flex flex-wrap gap-4 mb-8">
                            {spa.google_maps_url && (
                                <a
                                    href={spa.google_maps_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-gold flex items-center gap-2"
                                    data-testid="google-maps-link"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    View on Google Maps
                                </a>
                            )}
                            <a
                                href={`tel:${spa.phone}`}
                                className="btn-outline-gold"
                                data-testid="call-btn"
                            >
                                Call Now
                            </a>
                        </div>

                        {/* Services */}
                        <div className="mb-8">
                            <h3 className="font-heading text-xl text-[#4A4E4D] mb-4">Services</h3>
                            <div className="flex flex-wrap gap-2">
                                {spa.services.map((service, index) => (
                                    <span
                                        key={index}
                                        className="service-tag"
                                    >
                                        {service}
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Amenities */}
                        <div>
                            <h3 className="font-heading text-xl text-[#4A4E4D] mb-4">Amenities</h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {spa.amenities.map((amenity, index) => (
                                    <div
                                        key={index}
                                        className="amenity-badge"
                                    >
                                        <Check className="w-4 h-4 text-[#C8A86B]" />
                                        {amenity}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Map Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="mt-12"
                >
                    <h3 className="font-heading text-2xl text-[#4A4E4D] mb-6">Location</h3>
                    <MapView spas={[spa]} singleSpa={true} height="400px" />
                </motion.div>
            </div>
        </div>
    );
};

export default SpaDetails;
